import React, { useEffect, useRef, useState } from 'react';
import { Vehicle } from 'core/models/vehicle';
import { useVehicleStatus } from 'services/useVehicleStatus';
import { VehicleStatus as VehicleStatusView } from 'components/views/molecules/VehicleStatus';
import { useVehicle } from 'services/useVehicle';
import { Project } from 'core/models/project';
import { Channel } from 'core/models/channel';
import { useChannelStream } from 'services/useChannelStream';
import { cancellable } from 'services/utility/cancellable';
import { Environment } from 'core/models/environment';
import { useVehicleTelemetry } from 'services/useVehicleTelemetry';

const ONLINE_DETECTION_INTERVAL_MS = 1000; // [ms]

function useOnlineDetection(vehicleInfo: Vehicle.Status) {
    const [isOnline, setOnline] = useState(false);

    const timeout = useRef<number>();
    const count = useRef<number>(0);
    useEffect(() => {
        if (count.current == 0) {
            count.current++;
            return;
        }

        return cancellable(async isCancel => {
            if (isCancel()) return;
            window.clearTimeout(timeout.current);
            setOnline(true);
            timeout.current = window.setTimeout(() => {
                if (isCancel()) return;
                setOnline(false);
            }, ONLINE_DETECTION_INTERVAL_MS);
        });
    }, [vehicleInfo, timeout, count, setOnline]);

    return isOnline;
}

export type VehicleStatusProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
    darkMode?: boolean;
    channel?: Channel | undefined;
}

export const VehicleStatus = (props: VehicleStatusProps) => {
    const vehicle = useVehicle(props.vehicleId, props.projectId, props.environmentId);
    const vehicleInfo = useVehicleStatus(props.vehicleId, props.projectId);
    const vehicleTelemetry = useVehicleTelemetry(props.vehicleId, props.projectId);
    const isOnline = useOnlineDetection(vehicleInfo);

    useEffect(() => {
        console.info(`Autoware online status: ${isOnline}`);
    }, [isOnline]);

    const stream = useChannelStream(props.channel, true) ?? null;

    return (
        <VehicleStatusView name={vehicle ? vehicle.name : ''} darkMode={props.darkMode} {...vehicleInfo} status={vehicleTelemetry?.status} isOnline={isOnline} stream={stream} />
    );
};
